import { HStack, Text, VStack } from "@chakra-ui/layout";
import { Tooltip } from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { ColoredAmount, DataTable } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { currency } from "@src/utils/formatters";
import { observer } from "mobx-react-lite";
import type { Store } from "./Store";

export interface SummaryProps {
  summary: Store["projectsSummary"];
  projectsCount: number;
  isLoaded: boolean;
}

const Summary = ({ summary, projectsCount, isLoaded }: SummaryProps) => {
  const {
    total_budget,
    real_expenses,
    our_work_total_budget,
    our_work_spent_budget,
    our_work_credit_balance,
    our_work_credit_proforma_balance,
    expenses_credit_balance,
    expenses_credit_proforma_balance,
    uninvoiced,
    proforma_uninvoiced,
  } = summary ?? {
    total_budget: 0,
    real_expenses: 0,
    our_work_total_budget: 0,
    our_work_spent_budget: 0,
    our_work_credit_balance: 0,
    our_work_credit_proforma_balance: 0,
    expenses_credit_balance: 0,
    expenses_credit_proforma_balance: 0,
    uninvoiced: 0,
    proforma_uninvoiced: 0,
  };

  return (
    <DataTable.SummaryBox justify="space-between" hideBg>
      <DataTable.SummaryItem label={t`Projects`} isLoaded={isLoaded}>
        {projectsCount}
      </DataTable.SummaryItem>

      <DataTable.SummaryItem
        label={t`Budget`}
        isLoaded={isLoaded}
        labelTooltip={t`Total budget for our work and expenses`}
        alignment="right"
      >
        <VStack align="stretch">
          <HStack fontSize="sm" justify="space-between">
            <Text color="gray.500">
              <Trans>Total</Trans>
            </Text>

            <Text fontWeight="bold" color="black">
              {currency.formatByWorkspace(total_budget, {
                czkNoFractionDigits: true,
              })}
            </Text>
          </HStack>

          <HStack fontSize="sm" justify="space-between">
            <Text color="gray.500">
              <Trans>Work</Trans>
            </Text>

            <Text fontWeight="bold" color="black">
              {currency.formatByWorkspace(our_work_total_budget, {
                czkNoFractionDigits: true,
              })}
            </Text>
          </HStack>

          <HStack fontSize="sm" justify="space-between">
            <Text color="gray.500">
              <Trans>Expenses</Trans>
            </Text>

            <Text fontWeight="bold" color="black">
              {currency.formatByWorkspace(real_expenses, {
                czkNoFractionDigits: true,
              })}
            </Text>
          </HStack>
        </VStack>
      </DataTable.SummaryItem>

      <DataTable.SummaryItem
        label={t`Work spend`}
        isLoaded={isLoaded}
        labelTooltip={t`Tracked hours x Client hourly rate`}
        alignment="right"
      >
        {currency.formatByWorkspace(our_work_spent_budget, {
          czkNoFractionDigits: true,
        })}
      </DataTable.SummaryItem>

      <DataTable.SummaryItem label={t`Credit balance`} alignment="right">
        <HStack justify="space-between" fontSize="sm" align="start">
          <Text color="gray.500">
            <Trans>Work</Trans>&nbsp;
            <Tooltip
              shouldWrapChildren
              hasArrow
              placement="top"
              label={t`Billed amount minus work spent. Top figure includes all issued documents — proformas and payment receipts. Bottom figure counts settlement and standard invoices only.`}
            >
              <Icon
                boxSize="3"
                variant="solid"
                name="info-circle"
                color="gray.400"
              />
            </Tooltip>
          </Text>

          <VStack align="flex-end" spacing="0">
            <ColoredAmount value={our_work_credit_proforma_balance}>
              {currency.formatByWorkspace(our_work_credit_proforma_balance, {
                czkNoFractionDigits: true,
              })}
            </ColoredAmount>

            <ColoredAmount value={our_work_credit_balance}>
              {currency.formatByWorkspace(our_work_credit_balance, {
                czkNoFractionDigits: true,
              })}
            </ColoredAmount>
          </VStack>
        </HStack>

        <HStack justify="space-between" fontSize="sm" align="start" mt="1">
          <Text color="gray.500">
            <Trans>Expenses</Trans>&nbsp;
            <Tooltip
              shouldWrapChildren
              hasArrow
              placement="top"
              label={t`Billed amount minus expenses received. Top figure includes all issued documents — proformas and payment receipts. Bottom figure counts settlement and standard invoices only.`}
            >
              <Icon
                boxSize="3"
                variant="solid"
                name="info-circle"
                color="gray.400"
              />
            </Tooltip>
          </Text>

          <VStack align="flex-end" spacing="0">
            <ColoredAmount value={expenses_credit_proforma_balance}>
              {currency.formatByWorkspace(expenses_credit_proforma_balance, {
                czkNoFractionDigits: true,
              })}
            </ColoredAmount>

            <ColoredAmount value={expenses_credit_balance}>
              {currency.formatByWorkspace(expenses_credit_balance, {
                czkNoFractionDigits: true,
              })}
            </ColoredAmount>
          </VStack>
        </HStack>
      </DataTable.SummaryItem>

      <DataTable.SummaryItem
        label={t`Uninvoiced`}
        isLoaded={isLoaded}
        labelTooltip={t`Budget not yet billed. Top figure includes all issued documents — proformas and payment receipts. Bottom figure counts settlement and standard invoices only.`}
        alignment="right"
      >
        {currency.formatByWorkspace(proforma_uninvoiced, {
          czkNoFractionDigits: true,
        })}

        <br />

        {currency.formatByWorkspace(uninvoiced, {
          czkNoFractionDigits: true,
        })}
      </DataTable.SummaryItem>
    </DataTable.SummaryBox>
  );
};

export default observer(Summary);
